/**
 * 【法币展示/决策 统一逻辑】trade 页法币 tab 展示
 * 统一只信 recommendedCurrencyList，按 plates 中是否包含 258 判断法币，
 * 且仅 isSymbolExist=1 的法币可参与 Fiat-M / 默认交易对逻辑。
 */
import { Storage } from "@xt/xtbase";

const RECOMMENDED_FIAT_PLATE_ID = 258;

function hasFiatTradeSymbol(item: any): boolean {
  return Number(item?.isSymbolExist) === 1;
}

function getCurrenciesFromStorage(key: string): any[] {
  const data = Storage.get(key) || {};
  return (data as any)?.currencies || [];
}

export function getDynamicFiatCodes(): string[] {
  const source = getCurrenciesFromStorage("recommendedCurrencyList");
  const codes: string[] = [];
  source.forEach((item: any) => {
    const code = String(item?.currency || item?.currencyCode || item?.coinName || "").toUpperCase();
    const plates = Array.isArray(item?.plates) ? item.plates : [];
    const isFiat = plates.some((plateId: number | string | { id?: number | string }) => Number((plateId as any)?.id ?? plateId) === RECOMMENDED_FIAT_PLATE_ID);
    if (isFiat && hasFiatTradeSymbol(item) && code && !codes.includes(code)) {
      codes.push(code);
    }
  });
  return codes;
}

export function getDynamicFiatTabCodes(): string[] {
  return getDynamicFiatCodes().map((code) => code.toLowerCase());
}

export function isFiatMarketCode(code?: string | null): boolean {
  if (!code) return false;
  return getDynamicFiatCodes().includes(String(code).toUpperCase());
}

/** 与 Market USDT-M / cfdTabListSource 一致：动态法币 + 默认 AED/BRL/SAR */
const FIAT_QUOTE_FALLBACK = ["AED", "BRL", "SAR"];

/** 是否为法币计价（U 本位应排除） */
export function isFiatQuoteCurrency(code?: string | null): boolean {
  if (!code) return false;
  const upper = String(code).toUpperCase();
  if (isFiatMarketCode(upper)) return true;
  return FIAT_QUOTE_FALLBACK.includes(upper);
}

/**
 * 从 Storage 的币种列表构建 coinId -> 法币代码 的映射（与 ssr-web-index getFiatCurrencyMapFromStorage 一致）
 * Storage key：currencies，结构 { version?, currencies: [{ id, currency }] }（与 index 的 currencyList 一致）
 */
function getFiatCurrencyMapFromStorage(): Record<string, string> {
  const currencies = getCurrenciesFromStorage("recommendedCurrencyList");
  const map: Record<string, string> = {};
  currencies.forEach((item: any) => {
    const id = item.id ?? item.currencyId ?? item.coinId;
    const code = item.currency ?? item.currencyCode;
    if (id != null && code) {
      const upper = (code + "").toUpperCase();
      map[id] = upper;
      map[String(id)] = upper;
      map[Number(id)] = upper;
    }
  });
  return map;
}

/**
 * 根据 getUserInfo 返回的 fiatCurrencyIds，用 recommendedCurrencyList 映射得到允许的法币代码
 */
export function getFiatCodesFromFiatCurrencyIds(fiatCurrencyIds?: number[] | null): string[] {
  if (!fiatCurrencyIds || !Array.isArray(fiatCurrencyIds) || fiatCurrencyIds.length === 0) {
    return [];
  }
  const map = getFiatCurrencyMapFromStorage();
  const codes: string[] = [];
  fiatCurrencyIds.forEach((rawId) => {
    const code = map[rawId] ?? map[String(rawId)] ?? map[Number(rawId)];
    if (code && !codes.includes(code)) {
      codes.push(code);
    }
  });
  const dynamicCodes = getDynamicFiatCodes();
  return codes.filter((c) => dynamicCodes.includes(c));
}

/**
 * 获取允许展示的法币 tab 代码列表（仅来自 recommendedCurrencyList）
 */
export async function getAllowedFiatCodesForTabs(
  fiatCurrencyIds?: number[] | null,
  fiatCodesWithBalance?: string[]
): Promise<string[]> {
  return getDynamicFiatTabCodes();
}
